import React from 'react';
import ReactDOM from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import { Auth0Provider } from '@auth0/auth0-react';
import App from './App.tsx';
// Self-hosted fonts (replaces render-blocking Google Fonts link)
import '@fontsource/anton/400.css';
import '@fontsource-variable/archivo';
import '@fontsource/space-mono/400.css';
import '@fontsource/space-mono/700.css';
import './styles/global.css';

// flag-icons inlines ~260 country flags as SVG data URIs (~90 KiB gzipped) —
// far too heavy to sit in the render-blocking critical CSS. Flags are purely
// decorative (profiles, leaderboards), so load the stylesheet lazily after
// first paint as its own async chunk instead of importing it statically.
const loadFlagIcons = () => import('flag-icons/css/flag-icons.min.css');
if ('requestIdleCallback' in window) {
  requestIdleCallback(() => loadFlagIcons());
} else {
  setTimeout(loadFlagIcons, 1500);
}

// Detect when a new SW version is available
if ('serviceWorker' in navigator) {
  const setupUpdateListener = (registration: ServiceWorkerRegistration) => {
    registration.addEventListener('updatefound', () => {
      const newWorker = registration.installing;

      newWorker?.addEventListener('statechange', () => {
        // New SW installed and waiting (old SW still in control)
        if (newWorker.state === 'installed' && navigator.serviceWorker.controller) {
          // Dispatch custom event that App can listen to
          window.dispatchEvent(
            new CustomEvent('sw-update-available', {
              detail: { registration },
            })
          );
        }
      });
    });
  };

  // Check for updates on initial load
  navigator.serviceWorker.ready.then((registration) => {
    setupUpdateListener(registration);
    registration.update(); // Check immediately
  });

  // Export update check for navigation
  (window as any).checkForSWUpdate = () => {
    navigator.serviceWorker.getRegistrations().then((registrations) => {
      registrations.forEach((reg) => {
        setupUpdateListener(reg);
        reg.update();
      });
    });
  };
}

ReactDOM.createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    <Auth0Provider
      domain={import.meta.env.VITE_AUTH0_DOMAIN}
      clientId={import.meta.env.VITE_AUTH0_CLIENT_ID}
      authorizationParams={{
        redirect_uri: window.location.origin,
        audience: import.meta.env.VITE_AUTH0_AUDIENCE,
      }}
      useRefreshTokens={true}
      useRefreshTokensFallback={true}
      cacheLocation="localstorage"
    >
      <BrowserRouter>
        <App />
      </BrowserRouter>
    </Auth0Provider>
  </React.StrictMode>,
);
